import { t } from 'i18next';
import Swal, { SweetAlertResult } from 'sweetalert2';

import * as apiPostSearch from 'api/ingredient/PostIngredientSearchV1';
import { UrlParams } from 'enums/Ingredient/Search/UrlParams';
import { IngredientSortOrder } from 'enums/SortOrder/IngredientSortOrder';
import { AirUrlParams, AirUrlParamsClass } from 'hooks/useAirNavigation';
import * as AllergenList from 'types/Ingredient/Search/AllergenList';
import * as Ingredient from 'types/Ingredient/Search/Ingredient';
import * as SliderWithIdList from 'types/Ingredient/Search/SliderWithIdList';
import * as NutriScoreLabelList from 'types/NutriScore/NutriScoreLabelList';
import { PreferredAllergen } from 'types/User/UserProfile/UserSettings';
import * as StringList from 'types/_general/Search/StringList';

const defaultPageSize = 20;
const defaultPageIndex = 0;
const defaultSortOrder = IngredientSortOrder.Relevance;

export type Type = {
	allergenIds: StringList.Type;
	baseIngredientId: string | null;
	categoryIds: StringList.Type;
	characteristics: StringList.Type;
	co2LabelIds: StringList.Type;
	nutriScoreLabelIds: StringList.Type;
	pageIndex: number;
	pageSize: number;
	preferredAllergens: PreferredAllergen[];
	preferredNutrientIds: string[];
	searchTerm: string;
	seasonIds: StringList.Type;
	showOnlyDeleted: boolean;
	sliders: SliderWithIdList.Type;
	sortOrder: IngredientSortOrder;
	tagIds: StringList.Type;
};

export function create(): Type {
	return {
		allergenIds: StringList.create(),
		baseIngredientId: null,
		categoryIds: StringList.create(),
		characteristics: StringList.create(),
		co2LabelIds: StringList.create(),
		nutriScoreLabelIds: StringList.create(),
		pageIndex: defaultPageIndex,
		pageSize: defaultPageSize,
		preferredAllergens: [],
		preferredNutrientIds: [],
		searchTerm: '',
		seasonIds: StringList.create(),
		showOnlyDeleted: false,
		sliders: SliderWithIdList.create(),
		sortOrder: defaultSortOrder,
		tagIds: StringList.create(),
	};
}

export function reset(): Type {
	return create();
}

export function changeBaseIngredient(obj: Type, input: string | null): Type {
	return { ...obj, baseIngredientId: input };
}

export function changeCategories(obj: Type, items: string[]): Type {
	return { ...obj, categoryIds: items };
}

export function changeSeasons(obj: Type, items: string[]): Type {
	return { ...obj, seasonIds: items };
}

export function changeTags(obj: Type, items: string[]): Type {
	return { ...obj, tagIds: items };
}

export function changeAllergens(obj: Type, items: StringList.Type): Type {
	return { ...obj, allergenIds: items };
}

export function changeNutriScoreLabels(
	obj: Type,
	input: { saved: string; all: NutriScoreLabelList.Type }
): Type {
	const result = [...obj.nutriScoreLabelIds];
	const found = input.all.filter((x) => x.label == input.saved);

	found.forEach((x) => {
		x.ids.forEach((y) => {
			if (result.includes(y)) {
				const index = result.findIndex((x) => x === y);
				result.splice(index, 1);
			} else {
				result.push(y);
			}
		});
	});

	return { ...obj, nutriScoreLabelIds: result };
}

export function changeCharacteristics(obj: Type, input: { saved: string; all: string[] }): Type {
	const resultCharacteristics = [...obj.characteristics];

	if (resultCharacteristics.length === 0)
		return { ...obj, characteristics: input.all.filter((x) => x != input.saved) };
	if (resultCharacteristics.length === 1 && resultCharacteristics.includes(input.saved))
		return { ...obj, characteristics: input.all.filter((x) => x != input.saved) };
	if (resultCharacteristics.includes(input.saved))
		return { ...obj, characteristics: resultCharacteristics.filter((x) => x != input.saved) };
	resultCharacteristics.push(input.saved);
	return { ...obj, characteristics: resultCharacteristics };
}

export function changeSlider(
	obj: Type,
	input: {
		id: string;
		left: number | null;
		right: number | null;
		maximum: number | null;
		minimum: number | null;
	}
): Type {
	return { ...obj, sliders: SliderWithIdList.changeSlider(obj.sliders, input) };
}

export function changeCo2Labels(obj: Type, input: string): Type {
	const resultLabels = obj.co2LabelIds;
	if (resultLabels.includes(input)) {
		return { ...obj, co2LabelIds: resultLabels.filter((x) => x != input) };
	}
	resultLabels.push(input);
	return { ...obj, co2LabelIds: resultLabels };
}

export function changePageIndex(obj: Type, input: number): Type {
	return { ...obj, pageIndex: input };
}

export function changePageSize(obj: Type, input: string): Type {
	return { ...obj, pageSize: Number(input) };
}

export function changeSortOrder(obj: Type, input: string): Type {
	return { ...obj, sortOrder: input as IngredientSortOrder };
}

export function changeSearchTerm(obj: Type, input: string): Type {
	return { ...obj, searchTerm: input };
}

//TODO find nice solution
export function mapToUrlParamsClass(obj: Type): AirUrlParamsClass {
	const output: AirUrlParams = {};

	if (obj.pageIndex != defaultPageIndex) output[UrlParams.PageIndex] = String(obj.pageIndex);
	if (obj.pageSize != defaultPageSize) output[UrlParams.PageSize] = String(obj.pageSize);
	if (obj.sortOrder != defaultSortOrder) output[UrlParams.SortOrder] = String(obj.sortOrder);
	if (obj.searchTerm != '') output[UrlParams.SearchTerm] = String(obj.searchTerm);
	if (obj.showOnlyDeleted) output[UrlParams.ShowOnlyDeleted] = String(obj.showOnlyDeleted);
	if (obj.nutriScoreLabelIds.length > 0)
		output[UrlParams.NutriScoreLabels] = StringList.mapToUrl(obj.nutriScoreLabelIds);
	if (obj.characteristics.length > 0)
		output[UrlParams.Characteristics] = StringList.mapToUrl(obj.characteristics);
	if (obj.sliders.length > 0) output[UrlParams.Slider] = SliderWithIdList.mapToUrl(obj.sliders);
	if (obj.allergenIds.length > 0)
		output[UrlParams.Allergen] = StringList.mapToUrl(obj.allergenIds);
	if (obj.baseIngredientId) output[UrlParams.BaseIngredient] = String(obj.baseIngredientId);
	if (obj.co2LabelIds.length > 0)
		output[UrlParams.Co2Label] = StringList.mapToUrl(obj.co2LabelIds);
	if (obj.tagIds.length > 0) output[UrlParams.Tags] = String(StringList.mapToUrl(obj.tagIds));
	if (obj.seasonIds.length > 0)
		output[UrlParams.Seasons] = String(StringList.mapToUrl(obj.seasonIds));
	if (obj.categoryIds.length > 0)
		output[UrlParams.Categories] = String(StringList.mapToUrl(obj.categoryIds));

	return new AirUrlParamsClass(output);
}

export function getFromUrl(
	p: AirUrlParamsClass,
	preferredAllergens: PreferredAllergen[],
	preferredNutrientIds: string[]
): Type {
	const params = p.params;

	return {
		allergenIds: StringList.mapFromUrl(params, UrlParams.Allergen),
		baseIngredientId: params.bi ? params.bi : null,
		categoryIds: StringList.mapFromUrl(params, UrlParams.Categories),
		characteristics: StringList.mapFromUrl(params, UrlParams.Characteristics),
		co2LabelIds: StringList.mapFromUrl(params, UrlParams.Co2Label),
		nutriScoreLabelIds: StringList.mapFromUrl(params, UrlParams.NutriScoreLabels),
		pageIndex: params.pi ? Number(params.pi) : defaultPageIndex,
		pageSize: params.ps ? Number(params.ps) : defaultPageSize,
		preferredAllergens: preferredAllergens,
		preferredNutrientIds: preferredNutrientIds,
		searchTerm: params.st ? params.st : '',
		seasonIds: StringList.mapFromUrl(params, UrlParams.Seasons),
		showOnlyDeleted: params.sd ? Boolean(params.sd) : false,
		sliders: SliderWithIdList.mapFromUrl(params, UrlParams.Slider),
		sortOrder: params.so ? (params.so as IngredientSortOrder) : defaultSortOrder,
		tagIds: StringList.mapFromUrl(params, UrlParams.Tags),
	};
}

export function mapToApi(obj: Type): apiPostSearch.Request {
	return {
		...obj,
		...SliderWithIdList.mapToApi(obj.sliders),
		allergenIds: AllergenList.mapStringListToApi(obj.allergenIds),
		baseIngredient: obj.baseIngredientId,
		carbonDioxideLabelIds: obj.co2LabelIds,
		nutriScoreLabelIds: obj.nutriScoreLabelIds,
		seasonIds: obj.seasonIds,
		tagIds: obj.tagIds,
	};
}

export async function restore(obj: Type, ingredientId: string): Promise<Type> {
	const result: SweetAlertResult = await Swal.fire({
		cancelButtonText: t('_general:CANCEL'),
		confirmButtonColor: '#00cc07',
		confirmButtonText: t('_general:RESTORE'),
		icon: 'warning',
		iconColor: '#00cc07',
		showCancelButton: true,
		text: t('ingredient:RESTORE_DIALOG'),
		title: t('ingredient:RESTORE_TITLE'),
	});
	if (result.value) {
		await Ingredient.callApiRestore(ingredientId);
		await Swal.fire({
			confirmButtonColor: '#00cc07',
			title: t('ingredient:RESTORE_CONFIRMATION'),
		});
	}

	return { ...obj };
}

export async function archive(obj: Type, ingredientId: string): Promise<Type> {
	const result: SweetAlertResult = await Swal.fire({
		cancelButtonText: t('_general:CANCEL'),
		confirmButtonColor: '#ff0004',
		confirmButtonText: t('_general:ARCHIVE'),
		icon: 'warning',
		iconColor: '#ff0004',
		showCancelButton: true,
		text: t('ingredient:ARCHIVE_DIALOG'),
		title: t('ingredient:ARCHIVE_TITLE'),
	});
	if (result.value) {
		await Ingredient.callApiArchive(ingredientId);
		await Swal.fire({
			confirmButtonColor: '#00cc07',
			title: t('ingredient:ARCHIVE_CONFIRMATION'),
		});
	}

	return { ...obj };
}
